import React, { useState } from 'react';
import {
  Card,
  CardActionArea,
  CardContent,
  Typography,
  CircularProgress,
  Box,
  useTheme,
  Tooltip,
} from '@mui/material';
import HttpIcon from '@mui/icons-material/Http';
import { toggleHeaderCheckError } from '../../api/client';

function HeaderCheckErrorCard({ pipeline, isActive, onToggle }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [loading, setLoading] = useState(false);

  const hasHeaderCheckKey = !!pipeline?.header_check_key;

  const handleClick = async () => {
    if (!hasHeaderCheckKey) {
      return;
    }
    try {
      setLoading(true);
      await toggleHeaderCheckError(pipeline.id);
      if (onToggle) {
        await onToggle();
      }
    } catch (error) {
      console.error('Failed to toggle Header Check error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTooltipTitle = () => {
    if (!hasHeaderCheckKey) {
      return 'No header check key configured. Edit the pipeline to set a Header Check Key.';
    }
    return isActive
      ? 'Click to clear header group and restore normal state'
      : 'Click to set header group for header check error simulation';
  };

  return (
    <Tooltip title={getTooltipTitle()}>
      <Card
        sx={{
          background: isActive
            ? isDark
              ? 'rgba(0, 188, 212, 0.2)'
              : 'rgba(0, 188, 212, 0.1)'
            : hasHeaderCheckKey
            ? isDark
              ? 'rgba(255, 255, 255, 0.05)'
              : 'rgba(0, 0, 0, 0.02)'
            : isDark
            ? 'rgba(255, 255, 255, 0.03)'
            : 'rgba(0, 0, 0, 0.02)',
          border: isActive
            ? `2px solid ${isDark ? '#00bcd4' : '#00acc1'}`
            : `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          opacity: hasHeaderCheckKey ? 1 : 0.5,
          transition: 'all 0.3s ease',
          '&:hover': hasHeaderCheckKey
            ? {
                transform: 'translateY(-2px)',
                boxShadow: isActive
                  ? `0 4px 12px ${isDark ? 'rgba(0,188,212,0.3)' : 'rgba(0,188,212,0.3)'}`
                  : `0 4px 12px ${isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.1)'}`,
              }
            : {},
        }}
      >
        <CardActionArea
          onClick={handleClick}
          disabled={loading || !hasHeaderCheckKey}
          sx={{
            cursor: hasHeaderCheckKey ? 'pointer' : 'not-allowed',
          }}
        >
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 1.5,
              px: 1,
              minHeight: 80,
              justifyContent: 'center',
            }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: '#00bcd4' }} />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <HttpIcon
                  sx={{
                    fontSize: 28,
                    color: isActive
                      ? isDark
                        ? '#00bcd4'
                        : '#00acc1'
                      : hasHeaderCheckKey
                      ? isDark
                        ? 'rgba(255,255,255,0.3)'
                        : 'rgba(0,0,0,0.26)'
                      : isDark
                      ? 'rgba(255,255,255,0.2)'
                      : 'rgba(0,0,0,0.15)',
                    animation: isActive ? 'pulse 1.5s infinite' : 'none',
                    '@keyframes pulse': {
                      '0%': { opacity: 1 },
                      '50%': { opacity: 0.5 },
                      '100%': { opacity: 1 },
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: isActive
                      ? isDark
                        ? '#00bcd4'
                        : '#00acc1'
                      : hasHeaderCheckKey
                      ? 'text.secondary'
                      : 'text.disabled',
                    textAlign: 'center',
                    lineHeight: 1.2,
                  }}
                >
                  Header Check
                </Typography>
                {isActive && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.65rem',
                      color: isDark ? '#4dd0e1' : '#26c6da',
                    }}
                  >
                    ACTIVE
                  </Typography>
                )}
                {!hasHeaderCheckKey && !isActive && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.6rem',
                      color: 'text.disabled',
                    }}
                  >
                    Not configured
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
        </CardActionArea>
      </Card>
    </Tooltip>
  );
}

export default HeaderCheckErrorCard;
